import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import type { ApiEnvelope, ApiErrorEnvelope, AppError } from 'shared';
import { getDesktopBridge } from '@/desktop';

declare module 'axios' {
  interface AxiosRequestConfig {
    /** 由独立短期令牌授权的请求不能触发账户令牌刷新。 */
    skipAccessTokenRefresh?: boolean;
    sessionRevision?: number;
  }
}

const desktop = getDesktopBridge();
const configuredApiOrigin = (import.meta.env.VITE_API_ORIGIN as string | undefined)?.replace(
  /\/$/,
  '',
);
export const apiOrigin = desktop
  ? desktop.apiOrigin
  : (configuredApiOrigin ??
    (import.meta.env.DEV
      ? window.location.origin
      : ((import.meta.env.VITE_API_PROXY_TARGET as string | undefined)?.replace(/\/$/, '') ??
        window.location.origin)));
export const apiBaseURL = desktop || configuredApiOrigin ? `${apiOrigin}/api/v1` : '/api/v1';

const apiClient = axios.create({
  baseURL: apiBaseURL,
  withCredentials: true,
  headers: { Accept: 'application/json' },
});

let persistAccessToken = true;
let accessToken = localStorage.getItem('accessToken');
let refreshPromise: Promise<string | null> | null = null;
let refreshHandler: (() => Promise<string | null>) | null = null;
let sessionRevision = 0;
const accessTokenListeners = new Set<(token: string | null) => void>();

export function invalidateSessionRequests(): void { sessionRevision += 1; }
export function getSessionRevision(): number { return sessionRevision; }
export function sessionChanged(): AppError {
  return { code: 'SESSION_CHANGED', status: 0, message: '登录会话已变化，请重新操作', retryable: false };
}

export function setAccessToken(token: string | null): void {
  const changed = accessToken !== token;
  accessToken = token;
  if (persistAccessToken) {
    if (token) localStorage.setItem('accessToken', token);
    else localStorage.removeItem('accessToken');
  }
  if (changed) notifyAccessTokenChange(token);
}

/** 原生端 Access Token 仅保留在内存，Web 与 Desktop 保持原有持久化行为。 */
export function configureSessionClient(input: {
  persistAccessToken?: boolean;
  refresh?: (() => Promise<string | null>) | null;
}): void {
  persistAccessToken = input.persistAccessToken ?? persistAccessToken;
  refreshHandler = input.refresh ?? refreshHandler;
  if (!persistAccessToken) {
    localStorage.removeItem('accessToken');
    if (accessToken) {
      accessToken = null;
      notifyAccessTokenChange(null);
    }
  }
}

/** 网页保留现有持久化行为，APP 的 Access Token 仅存内存。 */
export function getAccessToken(): string | null {
  return accessToken;
}

/** 供认证状态和 WebSocket 等依赖方在令牌轮换后统一同步。 */
export function onAccessTokenChange(listener: (token: string | null) => void): () => void {
  accessTokenListeners.add(listener);
  return () => accessTokenListeners.delete(listener);
}

function notifyAccessTokenChange(token: string | null): void {
  accessTokenListeners.forEach((listener) => listener(token));
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== 'accessToken' || !persistAccessToken) return;
    // 事件可能排队，读取当前值避免旧事件把令牌倒退。
    const token = localStorage.getItem('accessToken');
    if (accessToken === token) return;
    accessToken = token;
    notifyAccessTokenChange(accessToken);
  });
}

apiClient.interceptors.request.use((config) => {
  config.sessionRevision = sessionRevision;
  const target = new URL(apiClient.getUri(config), window.location.href);
  const platformApi = target.origin === new URL(apiBaseURL, window.location.href).origin && target.pathname.startsWith('/api/v1/');
  if (!platformApi) {
    config.headers.delete('Authorization');
    config.headers.delete('X-Desktop-Device-Id');
    config.skipAccessTokenRefresh = true;
    return config;
  }
  if (desktop) config.headers['X-Desktop-Device-Id'] = desktop.deviceId;
  // Runtime Bridge 使用独立、短期的 Capability Token；调用方已显式传入 Authorization 时不得被账户 Token 覆盖。
  if (accessToken && !config.headers.Authorization)
    config.headers.Authorization = `Bearer ${accessToken}`;
  return config;
});

apiClient.interceptors.response.use(
  (response) => {
    if (response.config.sessionRevision !== sessionRevision) throw sessionChanged();
    return response;
  },
  async (error: AxiosError<ApiErrorEnvelope>) => {
    const request = error.config as
      (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
    if (request && request.sessionRevision !== sessionRevision) return Promise.reject(sessionChanged());
    if (
      error.response?.status === 401 &&
      request &&
      !request._retried &&
      !request.skipAccessTokenRefresh &&
      !request.url?.endsWith('/auth/refresh')
    ) {
      request._retried = true;
      const authorization = request.headers.Authorization;
      const token = await refreshAccessToken(
        typeof authorization === 'string' && authorization.startsWith('Bearer ')
          ? authorization.slice('Bearer '.length)
          : null,
      );
      if (request.sessionRevision !== sessionRevision) return Promise.reject(sessionChanged());
      if (token) {
        // error.config 保留了首次请求时的 Authorization；不覆盖会把已过期的旧 token 再发一次。
        request.headers.Authorization = `Bearer ${token}`;
        return apiClient.request(request);
      }
    }
    const appError = toAppError(error);
    if (appError.code === 'AI_CREDITS_INSUFFICIENT' && typeof window !== 'undefined')
      window.dispatchEvent(
        new CustomEvent('ai-credits-insufficient', { detail: appError.details }),
      );
    return Promise.reject(appError);
  },
);

/**
 * 同一标签页内的并发请求共用一次刷新，避免 Refresh Token Rotation 被重复消费。
 */
export async function refreshAccessToken(tokenBeforeRefresh = accessToken): Promise<string | null> {
  if (!refreshPromise) {
    // 迟到的 401 若使用旧令牌，复用当前令牌，不能再次撤销已刷新的会话。
    if (accessToken && accessToken !== tokenBeforeRefresh) return accessToken;
    const revisionBeforeRefresh = sessionRevision;
    refreshPromise = withRefreshLock(tokenBeforeRefresh, async () => {
      if (revisionBeforeRefresh !== sessionRevision) return getAccessToken();
      try {
        const token = refreshHandler
          ? await refreshHandler()
          : desktop
            ? ((await desktop.auth.refresh())?.accessToken ?? null)
            : (
                await axios.post<ApiEnvelope<{ accessToken: string }>>(
                  `${apiOrigin}/api/v1/auth/refresh`,
                  {},
                  { withCredentials: true },
                )
              ).data.data.accessToken;
        if (revisionBeforeRefresh !== sessionRevision || accessToken !== tokenBeforeRefresh)
          return getAccessToken();
        setAccessToken(token);
        return token;
      } catch (cause) {
        if (revisionBeforeRefresh !== sessionRevision || accessToken !== tokenBeforeRefresh)
          return getAccessToken();
        const error = toAppError(cause);
        if (error.status !== 401 && error.status !== 403) throw error;
        setAccessToken(null);
        return null;
      }
    }).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

async function withRefreshLock(
  tokenBeforeRefresh: string | null,
  refresh: () => Promise<string | null>,
): Promise<string | null> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  if (!locks) return refresh();
  return locks.request('ai-workspace-token-refresh', async () => {
    const tokenFromAnotherTab = persistAccessToken ? localStorage.getItem('accessToken') : null;
    if (tokenFromAnotherTab && tokenFromAnotherTab !== tokenBeforeRefresh) {
      setAccessToken(tokenFromAnotherTab);
      return tokenFromAnotherTab;
    }
    return refresh();
  });
}

export function toAppError(error: unknown): AppError {
  if (isAppError(error) && !axios.isAxiosError(error)) return error;
  if (axios.isAxiosError<ApiErrorEnvelope>(error)) {
    const payload = error.response?.data;
    const status = error.response?.status ?? 0;
    const timedOut = error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT';
    const code = String(
      payload?.code ??
        (timedOut ? 'REQUEST_TIMEOUT' : status === 0 ? 'NETWORK_ERROR' : `HTTP_${status}`),
    );
    return {
      code,
      status,
      message:
        payload?.message ??
        (timedOut
          ? (error.config?.timeoutErrorMessage ?? '请求超时，请稍后重试')
          : status === 0
            ? '网络连接失败，请检查服务状态'
            : '请求处理失败'),
      traceId: payload?.traceId,
      details: payload?.details,
      retryable: status === 0 || status === 429 || status >= 500,
    };
  }
  if (isApiErrorEnvelope(error)) {
    return {
      code: String(error.code),
      status: typeof error.code === 'number' ? error.code : 0,
      message: error.message,
      traceId: error.traceId,
      details: error.details,
      retryable: false,
    };
  }
  if (error instanceof Error)
    return { code: 'UNKNOWN_ERROR', status: 0, message: error.message, retryable: false };
  return { code: 'UNKNOWN_ERROR', status: 0, message: '发生未知错误', retryable: false };
}

function isAppError(value: unknown): value is AppError {
  return Boolean(
    value &&
    typeof value === 'object' &&
    typeof (value as AppError).code === 'string' &&
    typeof (value as AppError).message === 'string' &&
    typeof (value as AppError).status === 'number',
  );
}

function isApiErrorEnvelope(value: unknown): value is ApiErrorEnvelope {
  return Boolean(
    value &&
    typeof value === 'object' &&
    (typeof (value as ApiErrorEnvelope).code === 'string' ||
      typeof (value as ApiErrorEnvelope).code === 'number') &&
    typeof (value as ApiErrorEnvelope).message === 'string',
  );
}

export const http = {
  async get<T>(url: string, config?: Parameters<typeof apiClient.get>[1]): Promise<T> {
    return (await apiClient.get<ApiEnvelope<T>>(url, config)).data.data;
  },
  async post<T>(
    url: string,
    data?: unknown,
    config?: Parameters<typeof apiClient.post>[2],
  ): Promise<T> {
    return (await apiClient.post<ApiEnvelope<T>>(url, data, config)).data.data;
  },
  async patch<T>(
    url: string,
    data?: unknown,
    config?: Parameters<typeof apiClient.patch>[2],
  ): Promise<T> {
    return (await apiClient.patch<ApiEnvelope<T>>(url, data, config)).data.data;
  },
  async put<T>(
    url: string,
    data?: unknown,
    config?: Parameters<typeof apiClient.put>[2],
  ): Promise<T> {
    return (await apiClient.put<ApiEnvelope<T>>(url, data, config)).data.data;
  },
  async delete<T>(url: string, config?: Parameters<typeof apiClient.delete>[1]): Promise<T> {
    return (await apiClient.delete<ApiEnvelope<T>>(url, config)).data.data;
  },
  async getBlob(url: string, config?: Parameters<typeof apiClient.get>[1]): Promise<Blob> {
    return (await apiClient.get<Blob>(url, { ...config, responseType: 'blob' })).data;
  },
};
